// app/(app)/settings/index.tsx
import { useAppTheme } from "@/hooks/useAppTheme";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { toggleTheme } from "@/store/slices/themeSlice";
import { Ionicons } from "@expo/vector-icons";
import Constants from "expo-constants";
import * as WebBrowser from "expo-web-browser";
import { useState } from "react";
import {
  ScrollView,
  StyleSheet,
  Switch,
  Text,
  TouchableOpacity,
  View,
} from "react-native";

export default function SettingsScreen() {
  const { colors, spacing, mode } = useAppTheme();
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.auth);
  const [isDarkMode, setIsDarkMode] = useState(mode === "dark");

  const handleThemeToggle = (value: boolean) => {
    setIsDarkMode(value);
    dispatch(toggleTheme());
  };

  const openLink = async (url: string) => {
    await WebBrowser.openBrowserAsync(url);
  };

  const appVersion = Constants.expoConfig?.version || "1.0.0";
  const buildNumber = Constants.nativeBuildVersion || "1";

  const styles = StyleSheet.create({
    container: {
      flex: 1,
      backgroundColor: colors.background,
    },
    scrollContent: {
      padding: spacing.lg,
      paddingBottom: spacing.xl,
    },
    section: {
      marginBottom: spacing.xl,
    },
    sectionTitle: {
      fontSize: 18,
      fontWeight: "600",
      color: colors.text,
      marginBottom: spacing.md,
      paddingHorizontal: spacing.sm,
    },
    card: {
      backgroundColor: colors.background,
      borderRadius: 12,
      borderWidth: 1,
      borderColor: colors.border,
      overflow: "hidden",
    },
    profileCard: {
      alignItems: "center",
      paddingVertical: spacing.xl,
    },
    avatar: {
      width: 80,
      height: 80,
      borderRadius: 40,
      backgroundColor: colors.primary + "20",
      justifyContent: "center",
      alignItems: "center",
      marginBottom: spacing.md,
    },
    userName: {
      fontSize: 20,
      fontWeight: "600",
      color: colors.text,
      marginBottom: 4,
    },
    userEmail: {
      fontSize: 14,
      color: colors.textLight,
    },
    userRole: {
      fontSize: 12,
      backgroundColor: colors.primary + "20",
      paddingHorizontal: 12,
      paddingVertical: 4,
      borderRadius: 20,
      color: colors.primary,
      marginTop: spacing.sm,
    },
    row: {
      flexDirection: "row",
      justifyContent: "space-between",
      alignItems: "center",
      paddingVertical: spacing.md,
      paddingHorizontal: spacing.lg,
      borderBottomWidth: 1,
      borderBottomColor: colors.border,
    },
    rowLast: {
      borderBottomWidth: 0,
    },
    rowLeft: {
      flexDirection: "row",
      alignItems: "center",
      gap: spacing.md,
    },
    rowText: {
      fontSize: 16,
      color: colors.text,
    },
    versionText: {
      fontSize: 14,
      color: colors.textLight,
    },
    linkText: {
      fontSize: 16,
      color: colors.primary,
    },
    dangerText: {
      color: colors.danger,
    },
  });

  return (
    <ScrollView
      style={styles.container}
      contentContainerStyle={styles.scrollContent}
      showsVerticalScrollIndicator={false}
    >
      {/* Profile Section */}
      <View style={styles.section}>
        <View style={[styles.card, styles.profileCard]}>
          <View style={styles.avatar}>
            <Ionicons name="person-outline" size={40} color={colors.primary} />
          </View>
          <Text style={styles.userName}>{user?.ownerName || "User"}</Text>
          <Text style={styles.userEmail}>{user?.email || "Not logged in"}</Text>
          <Text style={styles.userRole}>{user?.userType || "Employee"}</Text>
        </View>
      </View>

      {/* Appearance Section */}
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Appearance</Text>
        <View style={styles.card}>
          <View style={styles.row}>
            <View style={styles.rowLeft}>
              <Ionicons name="moon-outline" size={24} color={colors.text} />
              <Text style={styles.rowText}>Dark Mode</Text>
            </View>
            <Switch
              value={isDarkMode}
              onValueChange={handleThemeToggle}
              trackColor={{ false: colors.border, true: colors.primary }}
              thumbColor={colors.background}
            />
          </View>
        </View>
      </View>

      {/* Legal Section */}
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Legal</Text>
        <View style={styles.card}>
          <TouchableOpacity
            style={styles.row}
            onPress={() => openLink("https://yourdomain.com/privacy-policy")}
          >
            <View style={styles.rowLeft}>
              <Ionicons
                name="document-text-outline"
                size={24}
                color={colors.text}
              />
              <Text style={styles.rowText}>Privacy Policy</Text>
            </View>
            <Ionicons
              name="chevron-forward"
              size={20}
              color={colors.textLight}
            />
          </TouchableOpacity>
          <TouchableOpacity
            style={[styles.row, styles.rowLast]}
            onPress={() => openLink("https://yourdomain.com/terms-of-service")}
          >
            <View style={styles.rowLeft}>
              <Ionicons
                name="newspaper-outline"
                size={24}
                color={colors.text}
              />
              <Text style={styles.rowText}>Terms & Conditions</Text>
            </View>
            <Ionicons
              name="chevron-forward"
              size={20}
              color={colors.textLight}
            />
          </TouchableOpacity>
        </View>
      </View>

      {/* About Section */}
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>About</Text>
        <View style={styles.card}>
          <View style={styles.row}>
            <View style={styles.rowLeft}>
              <Ionicons
                name="information-circle-outline"
                size={24}
                color={colors.text}
              />
              <Text style={styles.rowText}>App Version</Text>
            </View>
            <Text style={styles.versionText}>
              {appVersion} ({buildNumber})
            </Text>
          </View>
          <View style={[styles.row, styles.rowLast]}>
            <View style={styles.rowLeft}>
              <Ionicons name="build-outline" size={24} color={colors.text} />
              <Text style={styles.rowText}>SDK Version</Text>
            </View>
            <Text style={styles.versionText}>
              Expo {Constants.expoConfig?.sdkVersion}
            </Text>
          </View>
        </View>
      </View>

      {/* Optional: Logout (if not in drawer) – uncomment if needed */}
      {/* <View style={styles.section}>
          <TouchableOpacity style={styles.card} onPress={handleLogout}>
            <View style={styles.row}>
              <View style={styles.rowLeft}>
                <Ionicons name="log-out-outline" size={24} color={colors.danger} />
                <Text style={[styles.rowText, styles.dangerText]}>Logout</Text>
              </View>
            </View>
          </TouchableOpacity>
        </View> */}
    </ScrollView>
  );
}
